import { flow } from 'fp-ts/function';
import { useSyncExternalStore } from 'react';

export type LocalStorageField<T> = {
  key: string;
  getValue: () => T;
  setValue: (value: T) => void;
  // react hook using useSyncExternalStore
  useValue: () => T;
};

const useLocalStorageField = <T extends string>(key: string): T | null =>
  useSyncExternalStore<T | null>(
    (onStoreChange) => {
      const listener = (event: StorageEvent) => {
        if (event.key === key || event.key === null) {
          onStoreChange();
        }
      };
      window.addEventListener('storage', listener);
      return () => {
        window.removeEventListener('storage', listener);
      };
    },
    () => localStorage.getItem(key) as T | null,
  );

export const createLocalStorageField = <T extends string>(
  key: string,
): LocalStorageField<T | null> => ({
  key,
  getValue: () => localStorage.getItem(key) as T | null,
  setValue: (value) => {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
    // On localStoage.setItem, the storage event is only triggered on other tabs and windows.
    // So we manually dispatch a storage event to trigger the subscribe function on the current window as well.
    window.dispatchEvent(new StorageEvent('storage', { key, newValue: value }));
  },
  useValue: () => useLocalStorageField(key),
});

export const createParsedLocalStorageField = <
  T,
  TStoredString extends string = string,
>(
  key: string,
  parse: (value: TStoredString | null) => T,
  serialize: (value: T) => TStoredString | null,
): LocalStorageField<T> => {
  const baseField = createLocalStorageField<TStoredString>(key);
  return {
    key,
    getValue: flow(baseField.getValue, parse),
    setValue: flow(serialize, baseField.setValue),
    useValue: flow(baseField.useValue, parse),
  };
};
